import React from 'react';

type RowerProfileCompletionRingProps = {
  percent: number;
  size: number;
  children: React.ReactNode;
  className?: string;
  /** Accessible label for the ring (e.g. profile completion percent). */
  ariaLabel?: string;
};

const RowerProfileCompletionRing: React.FC<RowerProfileCompletionRingProps> = ({
  percent,
  size,
  children,
  className = '',
  ariaLabel,
}) => {
  const strokeWidth = size >= 80 ? 2.5 : 2;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.min(100, Math.max(0, percent)) / 100);
  const complete = percent >= 100;

  if (complete) {
    return (
      <div
        className={`relative inline-flex shrink-0 ${className}`}
        style={{ width: size, height: size }}
        aria-label={ariaLabel}
      >
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full -rotate-90"
          viewBox={`0 0 ${size} ${size}`}
          aria-hidden
        >
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            className="stroke-gray-200"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            className="stroke-rowing-blue"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={0}
          />
        </svg>
        <div
          className={`absolute flex items-center justify-center overflow-hidden rounded-full ${
            size >= 80 ? 'inset-1' : 'inset-[3px]'
          }`}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative inline-flex shrink-0 ${className}`}
      style={{ width: size, height: size }}
      aria-label={ariaLabel}
    >
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full -rotate-90"
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          className="stroke-gray-200"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          className="stroke-amber-500 transition-[stroke-dashoffset] duration-300 ease-out"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div
        className={`absolute flex items-center justify-center overflow-hidden rounded-full ${
          size >= 80 ? 'inset-1' : 'inset-[3px]'
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export default RowerProfileCompletionRing;
